/**
 * Eva Platform Overview (SF-3733, re-aligned in SF-3840).
 *
 * Read-only admin dashboard for platform-wide Eva usage. Re-aligned with the
 * company-detail page (SF-3834) conventions: a month/year PeriodSelector drives
 * every tile, table, and chart; two trend charts (a rolling 12-month monthly
 * chart and a selected-month-to-date daily chart with a live "today" bar for the
 * current month) replace the old single daily/monthly toggle; and the headline
 * tiles follow the same live-vs-rollup rule — current-month figures show a
 * "Live" chip wherever a live source exists, past months drop the chip and read
 * from the Mongo rollups.
 *
 * Tiles: Total Spend, Messages, Active Users, Spend per Active User, and Spend
 * per Active Company. (The old "Total Users" tile was dropped — SF-3840.)
 *
 * Data comes from sf-api `/admin/eva/usage/*` (SF-3731). Every field is
 * null-guarded and money strings are parsed only at display time. Access is
 * gated by `admin:eva:usage:read` via the access-control provider; unauthorized
 * users see a 403 and no request is made.
 */
import { useMemo, useState } from 'react';
import { useCan } from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import { useNavigate } from 'react-router-dom';
import { Alert, Checkbox, Col, Input, Result, Row, Space, Spin, Table, Tag, Typography } from 'antd';
import {
  BankOutlined,
  DollarOutlined,
  MessageOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import type { CrudFilters } from '@refinedev/core';

import { KpiTile } from '../../components/kpi/KpiTile';
import { LiveIndicator } from '../../components/kpi/LiveIndicator';
import { TrendChart, type TrendPoint } from '../../components/charts/TrendChart';
import { ClickableRowsHover, DataTable } from '../../components/table/DataTable';
import { useClickableRows } from '../../components/table/useClickableRows';
import { COMPANIES_META } from '../companies/constants';
import type { Company } from '../companies/list';
import { formatCurrency, formatNumber, percentChange } from '../../utils/format';
import {
  currentPeriod,
  priorPeriod,
  monthRange,
  monthlyTrendRange,
  toDateString,
} from '../../utils/date';
import { PeriodSelector } from './components/PeriodSelector';
import { EntryPointBreakdown } from './components/EntryPointBreakdown';
import { pickEntryPointRows } from './components/entryPointRows';
import {
  usePlatformTotals,
  usePlatformLive,
  usePlatformSeries,
  usePlatformEntryPoints,
  useTopCompanies,
  useTopTools,
  type TopCompanyRow,
  type TopToolRow,
  type SeriesPoint,
} from './api/evaUsageClient';

const { Title, Text } = Typography;

/** Number of companies shown in the top-companies table. */
const TOP_COMPANY_LIMIT = 5;

/** Number of tools shown in the top-tools table. */
const TOP_TOOL_LIMIT = 10;

/** Server-side filter (SF-3875) keeping only companies with Eva usage this month. */
const EVA_ACTIVE_FILTER = { field: 'eva_active', operator: 'eq', value: 'true' } as const;

/** Map series points to the cost line plotted by a trend chart. */
function toCostTrend(series: SeriesPoint[] | undefined): TrendPoint[] {
  return (series ?? []).map((point) => ({
    bucket: point.bucket,
    value: Number.isFinite(Number(point.costUsd)) ? Number(point.costUsd) : 0,
  }));
}

export const EvaOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [companySearch, setCompanySearch] = useState('');
  // The All Companies list starts with only companies using Eva this month (SF-5145); the
  // "Show companies without Eva usage" box widens it to every company.
  const [showWithoutEva, setShowWithoutEva] = useState(false);

  // Access control — resolve before issuing any usage request so unauthorized
  // users never trigger a GET (mirrors the companies/SSO pattern).
  const { data: canAccess, isLoading: canLoading } = useCan({
    resource: 'eva',
    action: 'list',
  });
  const enabled = canAccess?.can === true;

  // SF-3840: the page can show any month from May 2026 → current month. The
  // selector drives every tile/table/chart. Default to the current month so the
  // page lands on live data.
  //
  // Mirror the company-detail (SF-3834) derive-don't-freeze pattern: do NOT
  // freeze the default with `useState(currentPeriod())`. A tab left open across
  // a UTC month boundary would keep the old month in state, silently turn live
  // off, and show the prior month's rollup until the user re-picked. Instead we
  // store only an EXPLICIT past-month override; while it's null the selected
  // period is derived from `currentPeriod()` every render so it tracks the real
  // current month across the rollover. Selecting the current month clears the
  // override (stays auto-tracking); selecting a past month sets it (pinned).
  const livePeriod = currentPeriod();
  const [periodOverride, setPeriodOverride] = useState<string | null>(null);
  const selectedPeriod =
    periodOverride !== null && periodOverride !== livePeriod ? periodOverride : livePeriod;
  const handlePeriodChange = (period: string) =>
    setPeriodOverride(period === currentPeriod() ? null : period);
  const thisPeriod = selectedPeriod;
  const lastPeriod = priorPeriod(selectedPeriod);
  const isCurrentMonth = thisPeriod === livePeriod;

  // Derive the chart ranges on every render (like the period helpers above)
  // rather than memoizing with an empty dep list — otherwise a tab left open
  // across a UTC day/month boundary keeps requesting the original end date and
  // never picks up new buckets until a remount/reload. The daily range follows
  // the SELECTED month (clamped to today for the current month, full month for
  // past months); the monthly range stays a rolling 12-month window.
  const dailyRange = monthRange(selectedPeriod);
  const monthlyRange = monthlyTrendRange();

  const totalsQuery = usePlatformTotals(thisPeriod, { enabled });
  // LIVE current-month platform usage: chat-service-proxied counters so the
  // spend/messages tiles tick up in near real time. Only meaningful for the
  // current month, so gate the query on `isCurrentMonth` (past months read the
  // Mongo rollup). The live payload uses `daily`/`monthly` buckets (no `totals`
  // key) and carries `company_count` for the spend-per-company denominator.
  const liveQuery = usePlatformLive({ enabled: enabled && isCurrentMonth });
  const prevTotalsQuery = usePlatformTotals(lastPeriod, { enabled });
  const dailySeriesQuery = usePlatformSeries({ ...dailyRange, granularity: 'daily' }, { enabled });
  const monthlySeriesQuery = usePlatformSeries(
    { ...monthlyRange, granularity: 'monthly' },
    { enabled },
  );
  const topCompaniesQuery = useTopCompanies(thisPeriod, TOP_COMPANY_LIMIT, { enabled });
  // Platform-wide top tools for the selected month. The insights endpoint needs
  // an explicit period + granularity (no "this-month" keyword), so reuse the
  // selected period and pin granularity to monthly to match the KPI tiles.
  const topToolsQuery = useTopTools(thisPeriod, 'monthly', TOP_TOOL_LIMIT, { enabled });
  // Spend by AI feature (SF-4567): the saved monthly split, used for past months
  // and as the current month's fallback when the live split is unavailable.
  const entryPointsQuery = usePlatformEntryPoints(thisPeriod, { enabled });

  // All-companies table (SF-3799). Reuses the `companies` Refine resource — the
  // same source the companies list page uses — so any company is reachable from
  // the Eva screen, not just those with usage in the Top-5. syncWithLocation is
  // disabled to avoid URL-filter collisions with other table state on the page.
  const {
    tableProps: companiesTableProps,
    setFilters: setCompanyFilters,
    setCurrent: setCompanyPage,
  } = useTable<Company>({
    resource: 'companies',
    meta: COMPANIES_META,
    syncWithLocation: false,
    pagination: { pageSize: 20 },
    // Matches the unticked box, so the first load (and its total) is active companies only.
    filters: { initial: [EVA_ACTIVE_FILTER] },
    queryOptions: { enabled },
  });

  // Both the search box and the "Show companies without Eva usage" checkbox thread into the
  // SAME companies query. setCompanyFilters uses 'replace' mode, so rebuild the
  // full filter set from both controls on every change rather than pushing one
  // filter in isolation (otherwise toggling one would wipe the other). eva_active
  // is a server-side filter, so total/pagination reflect the filtered set.
  const applyCompanyFilters = (next: { search?: string; showWithoutEva?: boolean }) => {
    const search = next.search ?? companySearch;
    const withoutEva = next.showWithoutEva ?? showWithoutEva;
    const filters: CrudFilters = [];
    if (search) filters.push({ field: 'search', operator: 'contains', value: search });
    if (!withoutEva) filters.push(EVA_ACTIVE_FILTER);
    // A new filter set starts again from page 1; Refine's setFilters keeps the current page, so a
    // shorter list would otherwise open on an empty page.
    setCompanyPage(1);
    setCompanyFilters(filters, 'replace');
  };

  const handleCompanySearch = (value: string) => {
    setCompanySearch(value);
    applyCompanyFilters({ search: value });
  };

  const handleShowWithoutEvaChange = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    setShowWithoutEva(checked);
    applyCompanyFilters({ showWithoutEva: checked });
  };

  const totals = totalsQuery.data?.data;
  const live = liveQuery.data?.data;
  const prevTotals = prevTotalsQuery.data?.data;
  const dailySeries = dailySeriesQuery.data?.data;
  const monthlySeries = monthlySeriesQuery.data?.data;
  const topCompanies = topCompaniesQuery.data?.data;
  const topTools = topToolsQuery.data?.data;

  // Live current-month platform payload. Treat it as available only for the
  // current month, on a clean (non-error) response with a usable monthly cost —
  // React Query keeps the last successful `data` even when the latest poll
  // errors, so gating on `isError` keeps the displayed figure and the "Live"
  // badge from ever disagreeing. When live is unavailable we transparently fall
  // back to the Mongo rollup (`totals`) so the page still shows a figure.
  //
  // The `live.period === thisPeriod` check closes the UTC month-rollover window:
  // on the first render of a new month React Query still holds the PREVIOUS
  // month's successful payload (isError=false, monthly.costUsd present) until the
  // refetch lands. Without the period match that stale figure — and the "Live"
  // chip — would briefly leak onto the new month (and feed a bogus today-bar on
  // day 1). Matching the payload's own period to the selected period rejects it
  // until the fresh poll arrives, at which point we fall back to the rollup.
  const liveAvailable =
    isCurrentMonth &&
    !liveQuery.isError &&
    live?.monthly?.costUsd != null &&
    live?.period === thisPeriod;

  // Spend by AI feature (SF-4567): same live-vs-saved rule as the tiles. Live
  // counts only when it is this month's payload and carries the split (a
  // chat-service from before SF-4565 sends none, so the saved figures are used).
  const entryPoints = pickEntryPointRows({
    isCurrentMonth,
    live: liveAvailable ? live?.byEntryPoint : null,
    liveLoading: liveQuery.isLoading,
    liveError:
      liveQuery.isError || (!liveQuery.isLoading && !(liveAvailable && live?.byEntryPoint)),
    saved: entryPointsQuery.data?.data,
    savedLoading: entryPointsQuery.isLoading,
    savedError: entryPointsQuery.isError,
  });

  // Headline spend: live month-to-date when available, else the rollup total for
  // the selected period; dash only when both are absent.
  const liveCost = liveAvailable
    ? (live?.monthly?.costUsd ?? null)
    : (totals?.totals?.costUsd ?? null);
  const liveValue = formatCurrency(liveCost);
  const costNum = liveCost !== null && Number.isFinite(Number(liveCost)) ? Number(liveCost) : null;
  // Show the skeleton while we have NO usable figure yet and a source is still
  // fetching. For past months only the rollup query applies (the live query is
  // disabled, and a disabled React Query still reports `isLoading: true`, so it
  // must never gate a past-month tile). Once a value resolves we render it; we
  // dash only after every applicable source has settled and both are absent.
  const spendLoading = isCurrentMonth
    ? liveCost === null && (liveQuery.isLoading || totalsQuery.isLoading)
    : totalsQuery.isLoading;
  // Hint must derive from the SAME `liveAvailable` gate as the displayed value
  // so the two can never disagree (a successful-but-empty live response falls
  // back to the rollup figure AND the rollup hint together).
  const liveHint = liveAvailable
    ? 'Live spend for the current month, updated without waiting for the nightly cron'
    : isCurrentMonth
      ? 'Live usage is temporarily unavailable; showing the latest nightly rollup'
      : 'Spend for the selected month, from the nightly rollup';

  // Month-over-month delta computed on the SAME figure the tile displays
  // (`liveCost`), compared against last month's rollup, so the "vs last month"
  // percentage can never disagree with the headline amount above it.
  const prevCost = prevTotals?.totals?.costUsd;
  const costDelta = percentChange(
    costNum,
    prevCost !== undefined && prevCost !== null ? Number(prevCost) : null,
  );

  // Messages headline — linked to the SAME live source + `liveAvailable` gate as
  // spend (they go live / fall back together). `liveAvailable` is gated on
  // `monthly.costUsd`, but Messages reads a DIFFERENT field
  // (`monthly.messageCount`) — a partial live payload could carry a usable cost
  // but a null count, so normalize to null (never undefined).
  const messageCount = liveAvailable
    ? (live?.monthly?.messageCount ?? null)
    : (totals?.totals?.messageCount ?? null);
  const messagesLoading = isCurrentMonth
    ? messageCount == null && (liveQuery.isLoading || totalsQuery.isLoading)
    : totalsQuery.isLoading;
  const messageDelta = percentChange(messageCount, prevTotals?.totals?.messageCount ?? null);

  // Active users: the live platform payload has NO active-user count, so this is
  // always sourced from the Mongo rollup (no "Live" chip on this tile).
  const activeUsers = totals?.activeUserCount ?? null;
  const activeDelta = percentChange(activeUsers, prevTotals?.activeUserCount ?? null);

  // Spend per Active User = period spend ÷ active users; $0 when there are zero
  // active users (mirrors company-detail's Avg Active-User Spend). The numerator
  // can be live (current month), but the denominator only exists in the rollup —
  // so the tile shows the Live chip when the spend figure is live, with a hint
  // noting the user count comes from the latest rollup.
  const spendPerActiveUser = useMemo(() => {
    if (costNum === null || activeUsers === null) return null;
    return activeUsers > 0 ? costNum / activeUsers : 0;
  }, [costNum, activeUsers]);
  const spendPerUserLoading =
    spendPerActiveUser === null &&
    (isCurrentMonth ? liveQuery.isLoading || totalsQuery.isLoading : totalsQuery.isLoading);

  // Spend per Active Company = period spend ÷ companies with usage this period.
  // Current month uses the live `company_count`; past months use the
  // top-companies pagination total (the true count of company-scope rows for the
  // period, independent of the table's top-N limit). $0 when zero companies.
  const liveCompanyCount =
    liveAvailable && Number.isFinite(Number(live?.company_count))
      ? Number(live?.company_count)
      : null;
  const companyCount = liveAvailable
    ? liveCompanyCount
    : (topCompanies?.pagination?.total ?? null);
  const spendPerActiveCompany = useMemo(() => {
    if (costNum === null || companyCount === null) return null;
    return companyCount > 0 ? costNum / companyCount : 0;
  }, [costNum, companyCount]);
  const spendPerCompanyLoading =
    spendPerActiveCompany === null &&
    (isCurrentMonth
      ? liveQuery.isLoading || totalsQuery.isLoading || topCompaniesQuery.isLoading
      : totalsQuery.isLoading || topCompaniesQuery.isLoading);

  // Daily trend for the selected month from the Mongo daily rollups. The rollups
  // only cover CLOSED days (through yesterday) — today's bucket is missing/stale
  // until tonight's job runs. For the live current month, augment the chart with
  // a LIVE today-bar so the current day reflects real-time spend:
  // today-bar = live month-to-date − Σ(current-month daily-rollup days already
  // present, i.e. through yesterday). Reuses the live poll (no new endpoint).
  const dailyTrend = useMemo(() => {
    const base = toCostTrend(dailySeries?.series);
    if (!isCurrentMonth || !liveAvailable) return base;
    const liveMtd = Number(live?.monthly?.costUsd);
    if (!Number.isFinite(liveMtd)) return base;

    const today = toDateString(new Date());
    const monthPrefix = today.slice(0, 7); // "YYYY-MM"
    // Sum the current month's CLOSED daily rollups (every bucket in this month
    // strictly before today). Those are baked into the rollup; the remainder of
    // the live MTD is today's spend.
    const closedMonthToDate = base
      .filter((point) => point.bucket < today && point.bucket.startsWith(monthPrefix))
      .reduce((sum, point) => sum + (Number.isFinite(point.value) ? point.value : 0), 0);
    // Live MTD includes today; subtract the closed days to isolate today. Clamp
    // at 0 so rollup/live skew can't render a negative bar.
    const todayValue = Math.max(0, liveMtd - closedMonthToDate);

    // Replace today's bucket if the rollup already emitted a (stale) one, else
    // append it so the chart's right edge is the live today-bar.
    const idx = base.findIndex((p) => p.bucket === today);
    if (idx >= 0) {
      const next = base.slice();
      next[idx] = { ...next[idx], value: todayValue };
      return next;
    }
    return [...base, { bucket: today, value: todayValue }];
  }, [dailySeries, isCurrentMonth, liveAvailable, live?.monthly?.costUsd]);
  const monthlyTrend = useMemo(() => toCostTrend(monthlySeries?.series), [monthlySeries]);

  // The live query is supplementary and degrades to the rollup on its own, so
  // its failure is deliberately excluded from the page-level partial-load
  // warning — a missing live endpoint must not trigger the warning while the
  // rollup is serving the figures.
  const anyError =
    totalsQuery.isError ||
    prevTotalsQuery.isError ||
    dailySeriesQuery.isError ||
    monthlySeriesQuery.isError ||
    topCompaniesQuery.isError ||
    topToolsQuery.isError;

  const topCompanyColumns: ColumnsType<TopCompanyRow> = [
    {
      title: 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (name: string | null, row) => name || row.companyId || '\u2014',
    },
    {
      title: 'Spend',
      key: 'cost',
      align: 'right',
      render: (_v, row) => formatCurrency(row.totals?.costUsd),
    },
    {
      title: 'Messages',
      key: 'messages',
      align: 'right',
      render: (_v, row) => formatNumber(row.totals?.messageCount ?? null),
    },
    {
      title: 'Active Users',
      dataIndex: 'activeUserCount',
      key: 'activeUserCount',
      align: 'right',
      render: (count?: number) => formatNumber(count ?? null),
    },
  ];

  // Top tools columns (SF-3775). The insights rollups carry only a tool name and
  // an invocation count — no cost dimension — so this table stays two columns.
  const topToolColumns: ColumnsType<TopToolRow> = [
    {
      title: 'Tool',
      dataIndex: 'tool',
      key: 'tool',
      render: (tool: string | null) => tool || '\u2014',
    },
    {
      title: 'Usage Count',
      dataIndex: 'count',
      key: 'count',
      align: 'right',
      render: (count?: number) => formatNumber(count ?? null),
    },
  ];

  // All Companies list: rows open the company's Eva page, and look
  // clickable like the DataTable lists (SF-5146).
  const companyColumns: ColumnsType<Company> = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Internal Name',
      dataIndex: 'internal_name',
      key: 'internal_name',
      render: (v?: string) => v || '\u2014',
    },
    {
      title: 'Eva Usage',
      dataIndex: 'has_active_eva_usage',
      key: 'has_active_eva_usage',
      render: (active?: boolean) => (
        <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'None'}</Tag>
      ),
    },
  ];
  const clickableCompanies = useClickableRows(companyColumns, (record) =>
    navigate(`/eva/companies/${record._id || record.id}`),
  );

  if (canLoading) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;
  }

  if (!enabled) {
    return (
      <Result
        status="403"
        title="Not Authorized"
        subTitle={canAccess?.reason ?? 'You do not have permission to view Eva usage.'}
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Row justify="space-between" align="middle" wrap gutter={[16, 8]}>
        <Col flex="auto">
          <Title level={3} style={{ marginBottom: 0 }}>
            Eva Platform Overview
          </Title>
          <Text type="secondary">
            {isCurrentMonth ? 'Current month' : 'Historical'} ({selectedPeriod}) usage across the
            platform
          </Text>
        </Col>
        <Col>
          <PeriodSelector value={selectedPeriod} onChange={handlePeriodChange} disabled={!enabled} />
        </Col>
      </Row>

      {anyError && (
        <Alert
          type="warning"
          showIcon
          message="Some usage data could not be loaded"
          description="One or more Eva usage endpoints did not respond. Figures may be incomplete."
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <KpiTile
            title="Total Spend"
            value={liveValue}
            icon={<DollarOutlined />}
            loading={spendLoading}
            deltaPercent={costDelta}
            deltaCaption="vs last month"
            higherIsBetter
            badge={liveAvailable ? <LiveIndicator /> : undefined}
            hint={liveHint}
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <KpiTile
            title="Messages"
            value={formatNumber(messageCount)}
            icon={<MessageOutlined />}
            loading={messagesLoading}
            deltaPercent={messageDelta}
            deltaCaption="vs last month"
            higherIsBetter
            badge={liveAvailable ? <LiveIndicator /> : undefined}
            hint={
              liveAvailable
                ? 'Live message count for the current month, updated without waiting for the nightly cron'
                : isCurrentMonth
                  ? 'Live usage is temporarily unavailable; showing the latest nightly rollup'
                  : 'Messages for the selected month, from the nightly rollup'
            }
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <KpiTile
            title="Active Users"
            value={formatNumber(activeUsers)}
            icon={<UserOutlined />}
            loading={totalsQuery.isLoading}
            deltaPercent={activeDelta}
            deltaCaption="vs last month"
            higherIsBetter
            hint="Distinct users with Eva activity in the selected month"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <KpiTile
            title="Spend per Active User"
            value={formatCurrency(spendPerActiveUser)}
            icon={<TeamOutlined />}
            loading={spendPerUserLoading}
            badge={liveAvailable ? <LiveIndicator /> : undefined}
            hint={
              liveAvailable
                ? 'Live month-to-date spend ÷ active users (user count from the latest nightly rollup)'
                : 'Total spend ÷ active users for the selected month'
            }
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <KpiTile
            title="Spend per Active Company"
            value={formatCurrency(spendPerActiveCompany)}
            icon={<BankOutlined />}
            loading={spendPerCompanyLoading}
            badge={liveAvailable ? <LiveIndicator /> : undefined}
            hint={
              liveAvailable
                ? 'Live month-to-date spend ÷ companies active this month'
                : 'Total spend ÷ companies with usage in the selected month'
            }
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <TrendChart
            title="Monthly Spend (12 months)"
            data={monthlyTrend}
            granularity="monthly"
            loading={monthlySeriesQuery.isLoading}
            valueFormatter={(v) => formatCurrency(v)}
          />
        </Col>
        <Col xs={24} lg={12}>
          <TrendChart
            title={`Daily Spend (${isCurrentMonth ? 'this month to date' : selectedPeriod})`}
            data={dailyTrend}
            granularity="daily"
            loading={dailySeriesQuery.isLoading}
            valueFormatter={(v) => formatCurrency(v)}
          />
        </Col>
      </Row>

      <div>
        <Space style={{ marginBottom: 12 }}>
          <Title level={5} style={{ margin: 0 }}>
            Top {TOP_COMPANY_LIMIT} Companies by Spend
          </Title>
          <Tag color="purple">{thisPeriod}</Tag>
        </Space>
        <DataTable<TopCompanyRow>
          data={topCompanies?.companies ?? []}
          columns={topCompanyColumns}
          loading={topCompaniesQuery.isLoading}
          rowKey={(row, index) => row.companyId ?? row.companyName ?? `row-${index}`}
          pagination={false}
          emptyText="No company usage this period"
          onRowClick={(row) => {
            if (row.companyId) {
              navigate(`/eva/companies/${row.companyId}`, {
                state: { companyName: row.companyName },
              });
            }
          }}
        />
      </div>

      <EntryPointBreakdown
        title="Spend by AI Feature"
        period={thisPeriod}
        rows={entryPoints.rows}
        isLive={entryPoints.isLive}
        loading={entryPoints.loading}
        failed={entryPoints.failed}
      />

      <div>
        <Space style={{ marginBottom: 12 }}>
          <Title level={5} style={{ margin: 0 }}>
            Top {TOP_TOOL_LIMIT} Tools by Usage
          </Title>
          <Tag color="purple">{thisPeriod}</Tag>
        </Space>
        <DataTable<TopToolRow>
          data={topTools?.tools ?? []}
          columns={topToolColumns}
          loading={topToolsQuery.isLoading}
          rowKey={(row, index) => row.tool ?? `tool-${index}`}
          pagination={false}
          emptyText="No tool usage this period"
        />
      </div>

      <div>
        <Space style={{ marginBottom: 12 }}>
          <Title level={5} style={{ margin: 0 }}>
            All Companies
          </Title>
        </Space>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <Input
              placeholder="Search name or internal name..."
              prefix={<SearchOutlined />}
              value={companySearch}
              onChange={(e) => handleCompanySearch(e.target.value)}
              allowClear
              style={{ width: 320 }}
            />
            <Checkbox checked={showWithoutEva} onChange={handleShowWithoutEvaChange}>
              Show companies without Eva usage
            </Checkbox>
          </Space>
          <ClickableRowsHover>
            <Table
              {...companiesTableProps}
              size="middle"
              rowKey={(record) => record._id || record.id || record.internal_name || record.name}
              columns={clickableCompanies.columns}
              onRow={clickableCompanies.onRow}
            />
          </ClickableRowsHover>
        </Space>
      </div>
    </Space>
  );
};
